/* eslint-disable no-console */
import axios, {AxiosError} from 'axios';

import {CLIMATE_DB_BASE_URL, CLIMATE_DB_ENDPOINT_JSON} from '../../../config';
import CRUDMethods from '../../../constants/CRUDConstants';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';

import {ensureAIToken} from './aiTokenAPI';

export type OverviewInsightKind = 'yoy' | 'trend' | 'gap';

export interface OverviewInsightFactor {
  name: string;
  value: string;
  detail: string;
  tone: 'good' | 'watch' | 'neutral';
  direction: 'up' | 'down' | 'none';
}

export type OverviewInsightResult =
  | {
      state: 'ready';
      summary: string;
      factors: OverviewInsightFactor[];
      suggestion: string | null;
    }
  | {state: 'empty'}
  | {state: 'unavailable'}
  | {state: 'error'};

const INSIGHT_TYPE: Record<OverviewInsightKind, string> = {
  yoy: 'yoy_sustainability_ai_insight',
  trend: 'emission_trends_ai_insight',
  gap: 'gap_analysis_ai_insight'
};

type InsightBatch = 'dashboard' | 'gap';

// Dashboard charts need both cards, so they share one request. Gap analysis
// must not be included there: calculating all three together times out.
const BATCH_TYPES: Record<InsightBatch, string> = {
  dashboard: `${INSIGHT_TYPE.yoy},${INSIGHT_TYPE.trend}`,
  gap: INSIGHT_TYPE.gap
};

const batchFor = (kind: OverviewInsightKind): InsightBatch =>
  kind === 'gap' ? 'gap' : 'dashboard';

const UNAVAILABLE_STATUS = [404, 503];

interface InsightHighlight {
  text?: string;
  tone?: string;
  label?: string;
  direction?: string;
  metric?: {display?: string};
}

interface GeneratedInsight {
  status?: string;
  key?: string;
  insight_type?: string;
  message?: string | null;
  card?: {
    headline?: string | null;
    highlights?: InsightHighlight[];
    suggestion?: string | null;
  } | null;
}

interface GenerateResponse {
  insights?: GeneratedInsight[];
}

const readTone = (tone: string | undefined): OverviewInsightFactor['tone'] => {
  if (tone === 'good' || tone === 'watch') {
    return tone;
  }

  return 'neutral';
};

const readDirection = (
  direction: string | undefined
): OverviewInsightFactor['direction'] => {
  if (direction === 'up' || direction === 'down') {
    return direction;
  }

  return 'none';
};

const toFactors = (highlights: InsightHighlight[] | undefined) =>
  (highlights ?? []).flatMap((highlight) => {
    const name = highlight.label?.trim() ?? '';
    const detail = highlight.text?.trim() ?? '';

    if (!name || !detail) {
      return [];
    }

    return [
      {
        name,
        detail,
        value: highlight.metric?.display?.trim() ?? '',
        tone: readTone(highlight.tone),
        direction: readDirection(highlight.direction)
      }
    ];
  });

const toResult = (insight: GeneratedInsight | undefined): OverviewInsightResult => {
  if (!insight) {
    return {state: 'empty'};
  }

  const factors = toFactors(insight.card?.highlights);
  const suggestion = insight.card?.suggestion?.trim() ?? null;
  const headline = insight.card?.headline?.trim() ?? '';
  const message = insight.message?.trim() ?? '';

  if (insight.status === 'insufficient_data') {
    if (!message) {
      return {state: 'empty'};
    }

    return {state: 'ready', summary: message, factors: [], suggestion: null};
  }

  if (!headline && !factors.length && !suggestion) {
    return {state: 'empty'};
  }

  let summary = headline;

  if (!summary) {
    summary = message;
  }

  return {state: 'ready', summary, factors, suggestion};
};

const insightsByType = (insights: GeneratedInsight[]): Map<string, GeneratedInsight> => {
  const byType = new Map<string, GeneratedInsight>();

  insights.forEach((insight) => {
    const key = insight.insight_type ?? insight.key;

    if (key) {
      byType.set(key, insight);
    }
  });

  return byType;
};

const cachedInsights = new Map<string, Map<string, GeneratedInsight>>();
const inFlight = new Map<
  string,
  Promise<Map<string, GeneratedInsight> | null>
>();
let insightEpoch = 0;

const clearCachedInsights = (mongoCompanyId: string, year: string): void => {
  const prefix = `${mongoCompanyId}:${year}:`;

  Array.from(cachedInsights.keys()).forEach((key) => {
    if (key.startsWith(prefix)) {
      cachedInsights.delete(key);
      inFlight.delete(key);
    }
  });
};

const fetchGeneratedInsights = async (
  mongoCompanyId: string,
  year: string,
  batch: InsightBatch
): Promise<Map<string, GeneratedInsight> | null> => {
  const token = await ensureAIToken();

  if (!token) {
    return null;
  }

  const response = await axios.request<GenerateResponse>({
    method: CRUDMethods.GET,
    url: `${CLIMATE_DB_BASE_URL}${CLIMATE_DB_ENDPOINT_JSON.insightsGenerate}`,
    params: {
      insight_type: BATCH_TYPES[batch],
      mongo_company_id: mongoCompanyId,
      year
    },
    headers: {Authorization: `Bearer ${token}`}
  });

  return insightsByType(response.data?.insights ?? []);
};

/**
 * Dashboard loads year-on-year and emissions-trend together.
 * Gap analysis loads only `gap_analysis_ai_insight`.
 * `year` is the platform range (`"2024-2025"`).
 */
export const getOverviewInsight = async (
  kind: OverviewInsightKind,
  year: string
): Promise<OverviewInsightResult> => {
  const mongoCompanyId = useCompanyStore.getState().companyId?.trim() ?? '';
  const selectedYear = year?.trim() ?? '';

  if (!mongoCompanyId || !selectedYear) {
    return {state: 'empty'};
  }

  const batch = batchFor(kind);
  const cacheKey = `${mongoCompanyId}:${selectedYear}:${batch}`;

  try {
    const cached = cachedInsights.get(cacheKey);

    if (cached) {
      return toResult(cached.get(INSIGHT_TYPE[kind]));
    }

    const epochAtStart = insightEpoch;
    const pending =
      inFlight.get(cacheKey) ??
      fetchGeneratedInsights(mongoCompanyId, selectedYear, batch)
        .then((insights) => {
          if (epochAtStart === insightEpoch && insights) {
            cachedInsights.set(cacheKey, insights);
          }

          return insights;
        })
        .finally(() => {
          inFlight.delete(cacheKey);
        });

    inFlight.set(cacheKey, pending);

    const insights = await pending;

    if (!insights) {
      return {state: 'unavailable'};
    }

    return toResult(insights.get(INSIGHT_TYPE[kind]));
  } catch (err) {
    const status = (err as AxiosError)?.response?.status;

    if (status !== undefined && UNAVAILABLE_STATUS.includes(status)) {
      return {state: 'unavailable'};
    }

    console.log('climate db insight err:', err);

    return {state: 'error'};
  }
};

/**
 * Asks Climate DB to rebuild insights after emissions are recalculated.
 * `year` is the platform range (`"2024-2025"`).
 */
export const regenerateOverviewInsights = async (
  mongoCompanyId: string,
  year: string
): Promise<void> => {
  const companyId = mongoCompanyId.trim();
  const selectedYear = year.trim();
  const token = await ensureAIToken();

  if (!companyId || !selectedYear || !token) {
    return;
  }

  try {
    await axios.request({
      method: CRUDMethods.POST,
      url: `${CLIMATE_DB_BASE_URL}${CLIMATE_DB_ENDPOINT_JSON.insightsRegenerate}`,
      data: {
        mongo_company_id: companyId,
        year: selectedYear
      },
      headers: {Authorization: `Bearer ${token}`}
    });
    insightEpoch += 1;
    clearCachedInsights(companyId, selectedYear);
  } catch (err) {
    console.log('climate db insight regenerate err:', err);
  }
};
